// src/components/expenses/ExpenseFilters.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../context/AuthContext';
import { FunnelIcon, XMarkIcon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const CATEGORIES = [
    { value: '', label: 'Tutte le categorie' },
    { value: 'MATERIALS', label: '🧱 Materiali' },
    { value: 'EQUIPMENT', label: '🚜 Attrezzature' },
    { value: 'LABOR', label: '👷 Manodopera' },
    { value: 'FUEL', label: '⛽ Carburante' },
    { value: 'SUBCONTRACT', label: '🤝 Subappalto' },
    { value: 'TRANSPORT', label: '🚛 Trasporto' },
    { value: 'PERMITS', label: '📋 Permessi' },
    { value: 'ADMIN', label: '📁 Amministrazione' },
    { value: 'OTHER', label: '📦 Altro' },
];

const PAYMENT_METHODS = [
    { value: '',             label: 'Tutti i metodi' },
    { value: 'CASH',         label: '💵 Contanti' },
    { value: 'CARD',         label: '💳 Carta' },
    { value: 'WAVE',         label: '🌊 Wave' },
    { value: 'ORANGE_MONEY', label: '🟠 Orange Money' },
    { value: 'MTN_MONEY',    label: '🟡 MTN Money' },
];

const ic = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition';

const ExpenseFilters = ({ filters, onChange, onReset, totalResults }) => {
    const { t } = useTranslation();
    const { projects } = useProjects({ limit: 100 });
    const { organization } = useAuth();
    const currency = organization?.currency || 'GNF';
    const [showAdvanced, setShowAdvanced] = useState(false);

    const hasActiveFilters = Object.entries(filters).some(([k, v]) =>
        v !== '' && v !== null && v !== undefined && k !== 'page' && k !== 'limit'
    );

    const activeCount = Object.entries(filters).filter(([k, v]) =>
        v !== '' && v !== null && v !== undefined && k !== 'page' && k !== 'limit'
    ).length;

    const set = (key, value) => onChange({ ...filters, [key]: value, page: 1 });

    return (
        <div className="bg-white rounded-2xl border overflow-hidden">

            {/* Barra principale */}
            <div className="p-4 flex flex-col sm:flex-row gap-3">
                {/* Ricerca testo */}
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={filters.search || ''}
                        onChange={e => set('search', e.target.value)}
                        placeholder="Cerca per riferimento, descrizione, fornitore..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    />
                </div>

                {/* Categoria */}
                <select value={filters.category || ''} onChange={e => set('category', e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>

                {/* Validazione */}
                <select value={filters.validated || ''} onChange={e => set('validated', e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]">
                    <option value="">Tutte</option>
                    <option value="true">✅ Validate</option>
                    <option value="false">⏳ In attesa</option>
                </select>

                {/* Filtri avanzati toggle */}
                <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-xl text-sm font-medium transition whitespace-nowrap ${showAdvanced || activeCount > 1
                            ? 'border-blue-300 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                >
                    <FunnelIcon className="w-4 h-4" />
                    Filtri
                    {activeCount > 0 && (
                        <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">
                            {activeCount}
                        </span>
                    )}
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                </button>

                {/* Reset */}
                {hasActiveFilters && (
                    <button onClick={onReset}
                        className="flex items-center gap-1.5 px-3 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-xl text-sm font-medium transition whitespace-nowrap">
                        <XMarkIcon className="w-4 h-4" />
                        Reset
                    </button>
                )}
            </div>

            {/* Filtri avanzati */}
            {showAdvanced && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

                        {/* Progetto */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Progetto</label>
                            <select value={filters.projectId || ''} onChange={e => set('projectId', e.target.value)} className={ic}>
                                <option value="">Tutti i progetti</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                            </select>
                        </div>

                        {/* Metodo pagamento */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Metodo Pagamento</label>
                            <select value={filters.paymentMethod || ''} onChange={e => set('paymentMethod', e.target.value)} className={ic}>
                                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>

                        {/* Importo min */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Importo Min ({currency})</label>
                            <input type="number" value={filters.minAmount || ''} onChange={e => set('minAmount', e.target.value)}
                                className={ic} placeholder="0" min="0" step="1000" />
                        </div>

                        {/* Importo max */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Importo Max ({currency})</label>
                            <input type="number" value={filters.maxAmount || ''} onChange={e => set('maxAmount', e.target.value)}
                                className={ic} placeholder="999999999" min="0" step="1000" />
                        </div>

                        {/* Data inizio */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Data Da</label>
                            <input type="date" value={filters.startDate || ''} onChange={e => set('startDate', e.target.value)} className={ic} />
                        </div>

                        {/* Data fine */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Data A</label>
                            <input type="date" value={filters.endDate || ''} onChange={e => set('endDate', e.target.value)} className={ic} />
                        </div>

                        {/* Fornitore */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('expenses.supplier')}</label>
                            <input type="text" value={filters.supplier || ''} onChange={e => set('supplier', e.target.value)}
                                className={ic} placeholder="Nome fornitore..." />
                        </div>

                        {/* Ordinamento */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Ordina per</label>
                            <select value={filters.sortBy || 'createdAt'} onChange={e => set('sortBy', e.target.value)} className={ic}>
                                <option value="createdAt">Data creazione</option>
                                <option value="invoiceDate">Data fattura</option>
                                <option value="totalAmount_desc">Importo ↓</option>
                                <option value="totalAmount_asc">Importo ↑</option>
                                <option value="reference">Riferimento</option>
                            </select>
                        </div>
                    </div>

                    {/* Quick date presets */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-200">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider self-center">Periodo:</span>
                        {[
                            { label: 'Oggi', days: 0 },
                            { label: 'Ultimi 7 giorni', days: 7 },
                            { label: 'Ultimo mese', days: 30 },
                            { label: 'Ultimi 3 mesi', days: 90 },
                            { label: 'Quest\'anno', days: 365 },
                        ].map(preset => {
                            const end = new Date().toISOString().split('T')[0];
                            const start = new Date(Date.now() - preset.days * 86400000).toISOString().split('T')[0];
                            return (
                                <button key={preset.label}
                                    onClick={() => onChange({ ...filters, startDate: start, endDate: end, page: 1 })}
                                    className="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition">
                                    {preset.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Results bar */}
            {totalResults !== undefined && (
                <div className={`px-4 py-2 border-t bg-gray-50 flex items-center justify-between ${hasActiveFilters ? 'border-blue-100 bg-blue-50' : ''}`}>
                    <p className="text-xs text-gray-500">
                        {hasActiveFilters
                            ? <><span className="font-semibold text-blue-700">{totalResults}</span> risultati con i filtri applicati</>
                            : <><span className="font-semibold text-gray-700">{totalResults}</span> spese totali</>
                        }
                    </p>
                    {hasActiveFilters && (
                        <button onClick={onReset} className="text-xs text-blue-600 hover:underline font-medium">
                            Rimuovi filtri
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExpenseFilters;